import { partition } from 'ramda';
import { useCallback, useState } from 'react';

export const useBatchedFileUpload = <R>(upload: (file: File) => Promise<R>) => {
  const [files, setFiles] = useState<R[]>([]);
  const [errors, setErrors] = useState<string[]>();
  const [isLoading, setLoading] = useState(false);

  const uploadBatch = useCallback(
    async (files: File[]) => {
      setLoading(true);
      const mappedUploads = files.map(upload);
      const results = await Promise.allSettled(mappedUploads);

      const predict = (result: PromiseSettledResult<R>): result is PromiseFulfilledResult<R> =>
        result.status === 'fulfilled';
      const [fulfilled, rejected] = partition(predict, results);

      const uploadedFiles = (fulfilled as PromiseFulfilledResult<R>[]).map((success) => success.value);
      const errors = (rejected as PromiseRejectedResult[]).map((success) => `${success.reason}`);

      setFiles(uploadedFiles);
      setErrors(errors);
      setLoading(false);

      // Return the uploaded files so the caller can use them immediately
      return uploadedFiles;
    },
    [upload],
  );

  return { uploadBatch, isLoading, files, errors };
};
